// import React from 'react'

// function UserAcquisitionAndRetentionTrends() {
//   return (
//     <div>UserAcquisition&RetentionTrends</div>
//   )
// }

// export default UserAcquisitionAndRetentionTrends

import React, { useState, useMemo, useContext, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SortIcon from "@mui/icons-material/Sort";
import Papa from "papaparse";
import analyticsService from "../../../../../services/analyticsService";
import { AuthContext } from "../../../../../authentication/AuthContext";

const PURPLE = "#6D5DD2";

const UserAcquisitionRetentionDetails = () => {
  const { user } = useContext(AuthContext);
  const [filter, setFilter] = useState("month");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [trendApiData, setTrendApiData] = useState([]);
  const [loading, setLoading] = useState(false);

  const rowsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.client_id) return;
      try {
        setLoading(true);
        const response = await analyticsService.getUserAcquisitionRetentionTrends(user.client_id);
        if (response.success && response.data) {
          setTrendApiData(response.data);
        }
      } catch (error) {
        console.error("Error fetching user acquisition and retention trends:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.client_id]);

  // Table structured data
  const rows = trendApiData.map((item) => ({
    date: item.date,
    newUsers: item.new_customers || 0,
    returning: item.returning_customers || 0,
  }));

  // Extract chart data
  const dates = trendApiData.map(item => item.date);
  const newUsers = trendApiData.map(item => item.new_customers || 0);
  const returningUsers = trendApiData.map(item => item.returning_customers || 0);

  // Apply sorting + searching
  const processedRows = useMemo(() => {
    let data = rows.filter((r) =>
      r.date.toLowerCase().includes(search.toLowerCase())
    );

    data.sort((a, b) =>
      sortOrder === "asc"
        ? a[sortField] > b[sortField]
          ? 1
          : -1
        : a[sortField] < b[sortField]
        ? 1
        : -1
    );

    return data;
  }, [search, sortField, sortOrder, filter, rows]);

  // Pagination
  const totalPages = Math.ceil(processedRows.length / rowsPerPage);
  const paginated = processedRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Chart config
  const chartSeries = [
    { name: "New Users", data: newUsers },
    { name: "Returning Users", data: returningUsers },
  ];

  const chartOptions = {
    chart: {
      type: "area",
      toolbar: { show: true },
    },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: dates,
      labels: { rotate: -30 },
    },
    colors: [PURPLE, "#22C55E"],
    dataLabels: { enabled: false },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 0.5, opacityFrom: 0.4, opacityTo: 0.1 },
    },
  };

  // CSV Export
  const exportCSV = () => {
    const csv = Papa.unparse(processedRows);
    const file = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = "UserTrends.csv";
    link.click();
  };

  return (
    <Box sx={{ p: 3 }}>
    {loading ? (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    ) : (
      <>

    {/* Updated Back Button Matching Theme */}
      <Button
        onClick={() => window.history.back()}
        startIcon={<ArrowBackIcon sx={{ fontSize: "18px", color: "#4F46E5" }} />}
        sx={{
          mb: 2,
          border: "1px solid #D4D7E2",
          textTransform: "uppercase",
          fontSize: "12px",
          borderRadius: "6px",
          background: "#F8F9FF",
          color: "#4F46E5",
          "&:hover": { background: "#EEF2FF" },
          paddingX: "14px",
        }}
      >
        Back
      </Button>
      {/* 🔙 Back button
      <Button
        variant="contained"
        sx={{
          bgcolor: PURPLE,
          "&:hover": { bgcolor: "#5A4BCB" },
          textTransform: "none",
          mb: 2,
        }}
        startIcon={<ArrowBackIcon />}
        onClick={() => window.history.back()}
      >
        Back
      </Button> */}

      {/* Title + Filter row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          {/* <Typography variant="h5" fontWeight={700}>
            User Acquisition & Retention Trends
          </Typography> */}

        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Filter */}
          {/* <TextField
            select
            size="small"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                borderColor: PURPLE,
              },
            }}
          >
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="3months">Last 3 Months</MenuItem>
          </TextField> */}

          {/* Export
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              borderColor: PURPLE,
              color: PURPLE,
              textTransform: "none",
            }}
            onClick={exportCSV}
          >
            Export CSV
          </Button> */}
        </Box>
      </Box>

      {/* 📊 Chart */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: "12px" }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          New vs. Returning Users Daily Volume
        </Typography>

        <ReactApexChart type="area" height={400} options={chartOptions} series={chartSeries} />
      </Paper>

      {/* Search + Table */}
      <Paper sx={{ p: 3, borderRadius: "12px" }}>
        <TextField
          placeholder="Search by date..."
          size="small"
          sx={{ mb: 2, width: "260px" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#EFEAFF", fontWeight: 600 }}>
              <td style={{ padding: 12 }}>Date <SortIcon fontSize="small" /></td>
              <td style={{ padding: 12, textAlign: "center" }}>New Users</td>
              <td style={{ padding: 12, textAlign: "center" }}>Returning Users</td>
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 12 }}>{row.date}</td>
                <td style={{ padding: 12, textAlign: "center" }}>{row.newUsers}</td>
                <td style={{ padding: 12, textAlign: "center" }}>{row.returning}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            sx={{ textTransform: "none", color: "#666" }}
          >
            Prev
          </Button>

          <Typography fontWeight={600}>Page {page} of {totalPages}</Typography>

          <Button
            variant="contained"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#5A4BCB" }, textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      </Paper>
    </>
    )}
    </Box>
  );
};

export default UserAcquisitionRetentionDetails;
